import React, { useRef, useState } from "react";
import {
  alpha,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  CircleAlert,
  Download,
  Info,
  TriangleAlert,
  Upload,
  X,
} from "lucide-react";
import { onErrorMessage } from "@utils/utilsApi";
import { openToast } from "@utils/toastStore";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { ButtonSecondary } from "src/UI/ButtonSecondary";

const BANNER_TEXT_COLOR = "#000000";

export interface UploadModalBanner {
  title?: string;
  text?: string;
  downloadLabel: string;
  onDownload: () => void | Promise<unknown>;
  downloadVariant?: "outlined" | "contained";
  downloadIcon?: React.ReactNode;
  secondaryDownloadLabel?: string;
  onSecondaryDownload?: () => void | Promise<unknown>;
  secondaryDownloadVariant?: "outlined" | "contained";
  secondaryDownloadIcon?: React.ReactNode;
  actionsAlign?: "text" | "center";
}

export interface UploadModalDownloadAction {
  label: string;
  onDownload: () => void | Promise<unknown>;
  variant?: "primary" | "secondary";
  icon?: React.ReactNode;
}

interface UploadModalCstProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  accept?: string;
  banner?: UploadModalBanner;
  maxRecords?: number;
  downloadActions?: UploadModalDownloadAction[];
  canUpload?: boolean;
  dropzoneVariant?: "default" | "accent";
  onUpload: (file: File) => Promise<any>;
  onUploaded?: () => void;
  successMessage?: string;
  discardsVariant?: "default" | "light";
  // Se true la modale resta consultabile (banner e download) ma il
  // caricamento del file è disabilitato.
  uploadDisabled?: boolean;
}

interface DiscardsFile {
  data: any;
  filename: string;
  type: string;
  discarded?: number | null;
  total?: number | null;
}

const UploadModalCst: React.FC<UploadModalCstProps> = ({
  open,
  onClose,
  title,
  description,
  accept,
  banner,
  maxRecords,
  downloadActions,
  canUpload = true,
  dropzoneVariant = "default",
  onUpload,
  onUploaded,
  successMessage,
  discardsVariant = "default",
  uploadDisabled = false,
}) => {
  const { t } = useTranslation("upload-modal");
  const theme = useTheme();

  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSecondaryDownloading, setIsSecondaryDownloading] = useState(false);
  const [downloadingActions, setDownloadingActions] = useState<number[]>([]);
  const [discards, setDiscards] = useState<DiscardsFile | null>(null);

  const isAccentDropzone = dropzoneVariant === "accent";
  const accentDropzoneBorder = `url("data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='100%' height='100%'><rect width='100%' height='100%' fill='none' rx='12' ry='12' stroke='${theme.palette.primary.main}' stroke-width='4' stroke-dasharray='12 12'/></svg>`,
  )}")`;

  const handleDownload = async () => {
    if (!banner) return;
    setIsDownloading(true);
    try {
      await banner.onDownload();
    } catch {
      openToast({
        severity: "error",
        message: String(t("download-error")),
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSecondaryDownload = async () => {
    if (!banner?.onSecondaryDownload) return;
    setIsSecondaryDownloading(true);
    try {
      await banner.onSecondaryDownload();
    } catch {
      openToast({
        severity: "error",
        message: String(t("download-error")),
      });
    } finally {
      setIsSecondaryDownloading(false);
    }
  };

  const handleDownloadAction = async (
    action: UploadModalDownloadAction,
    index: number,
  ) => {
    setDownloadingActions((prev) => [...prev, index]);
    try {
      await action.onDownload();
    } catch {
      openToast({
        severity: "error",
        message: String(t("download-error")),
      });
    } finally {
      setDownloadingActions((prev) => prev.filter((i) => i !== index));
    }
  };

  const handleFile = async (file: File) => {
    if (uploadDisabled) return;
    setIsUploading(true);
    try {
      const response: any = await onUpload(file);
      onUploaded?.();

      const explicitDiscards: DiscardsFile | undefined = response?.discards;
      const explicitSuccessMessage: string | undefined =
        response?.successMessage;

      if (explicitDiscards) {
        setDiscards(explicitDiscards);
        openToast({
          severity: "warning",
          message: String(t("with-discards")),
        });
        return;
      }

      const contentDisposition: string | undefined =
        response?.headers?.["content-disposition"];

      if (contentDisposition && contentDisposition.includes("filename")) {
        setDiscards({
          data: response.data,
          filename: contentDisposition
            .split("filename=")[1]
            ?.replace(/["']/g, "")
            .trim(),
          type: response?.headers?.["content-type"] || "text/csv",
        });
        openToast({
          severity: "warning",
          message: String(t("with-discards")),
        });
      } else {
        openToast({
          severity: "success",
          message:
            explicitSuccessMessage || successMessage || String(t("success")),
        });
        onClose();
      }
    } catch (e: any) {
      openToast({
        severity: "error",
        message: onErrorMessage(e) || String(t("error")),
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadDiscards = () => {
    if (!discards) return;
    const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
    const blob = new Blob([bom, discards.data], { type: discards.type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = discards.filename || "discards.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = "";
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (isUploading || uploadDisabled) return;
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const bannerActions = banner && (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent:
          banner.actionsAlign === "center" ? "center" : "flex-start",
        gap: 1.5,
        flexWrap: "wrap",
        "& .MuiButton-root": {
          height: "40px",
          padding: "0 16px",
          fontSize: "0.875rem",
          whiteSpace: "nowrap",
        },
        "& .MuiButton-outlined.Mui-disabled": {
          background: "transparent",
          borderColor: "rgba(0, 0, 0, 0.35)",
          color: "rgba(0, 0, 0, 0.6)",
        },
        "& .MuiButton-contained.Mui-disabled": {
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
          opacity: 0.7,
        },
      }}
    >
      <Button
        variant={banner.downloadVariant ?? "outlined"}
        startIcon={
          isDownloading ? (
            <CircularProgress size={14} />
          ) : (
            (banner.downloadIcon ?? <Download size={16} />)
          )
        }
        onClick={handleDownload}
        disabled={isDownloading}
        sx={{
          textTransform: "none",
          fontWeight: 700,
          borderRadius: "8px",
          ...(banner.downloadVariant === "contained"
            ? {
                backgroundColor: theme.palette.background.default,
                color: theme.palette.text.primary,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: theme.palette.background.default,
                  boxShadow: "none",
                },
              }
            : {
                borderColor: BANNER_TEXT_COLOR,
                color: BANNER_TEXT_COLOR,
                "&:hover": {
                  borderColor: BANNER_TEXT_COLOR,
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }),
        }}
      >
        {banner.downloadLabel}
      </Button>
      {banner.onSecondaryDownload && banner.secondaryDownloadLabel && (
        <Button
          variant={banner.secondaryDownloadVariant ?? "outlined"}
          startIcon={
            isSecondaryDownloading ? (
              <CircularProgress size={14} />
            ) : (
              (banner.secondaryDownloadIcon ?? <Download size={16} />)
            )
          }
          onClick={handleSecondaryDownload}
          disabled={isSecondaryDownloading}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "8px",
            ...(banner.secondaryDownloadVariant === "contained"
              ? {
                  backgroundColor: theme.palette.background.default,
                  color: theme.palette.text.primary,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: theme.palette.background.default,
                    boxShadow: "none",
                  },
                }
              : {
                  borderColor: BANNER_TEXT_COLOR,
                  color: BANNER_TEXT_COLOR,
                  "&:hover": {
                    borderColor: BANNER_TEXT_COLOR,
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                }),
          }}
        >
          {banner.secondaryDownloadLabel}
        </Button>
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={isUploading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogTitle sx={{ padding: "20px 24px 8px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 700,
              color: theme.palette.text.primary,
            }}
          >
            {title}
          </Typography>
          <IconButton
            onClick={onClose}
            aria-label="close"
            disabled={isUploading}
            sx={{ color: theme.palette.text.primary }}
          >
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: "12px 24px 24px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {description && (
          <Typography
            sx={{ fontSize: "13px", color: theme.palette.text.primary }}
          >
            {description}
          </Typography>
        )}

        {banner && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              padding: "16px",
              borderRadius: "12px",
              backgroundColor: theme.palette.badge.info.background,
              border: `1px solid ${theme.palette.badge.info.border}`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                ...(maxRecords != null && { alignItems: "center" }),
              }}
            >
              {maxRecords != null ? (
                <CircleAlert
                  size={20}
                  color={theme.palette.badge.info.border}
                  style={{ flexShrink: 0 }}
                />
              ) : (
                <Info
                  size={18}
                  color={theme.palette.badge.info.border}
                  style={{ flexShrink: 0, marginTop: 2 }}
                />
              )}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  flex: 1,
                }}
              >
                {maxRecords != null && (
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 700,
                      color: BANNER_TEXT_COLOR,
                    }}
                  >
                    {t("max-records", { max: maxRecords })}
                  </Typography>
                )}
                {banner.title && (
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 700,
                      color: BANNER_TEXT_COLOR,
                    }}
                  >
                    {banner.title}
                  </Typography>
                )}
                {banner.text && (
                  <Typography
                    sx={
                      banner.title || maxRecords != null
                        ? { fontSize: "12px", color: BANNER_TEXT_COLOR }
                        : {
                            fontSize: "13px",
                            fontWeight: 700,
                            color: BANNER_TEXT_COLOR,
                          }
                    }
                  >
                    {banner.text}
                  </Typography>
                )}
                {banner.actionsAlign !== "center" && bannerActions}
              </Box>
            </Box>
            {banner.actionsAlign === "center" && bannerActions}
          </Box>
        )}

        {canUpload && (
          <Box
            role="button"
            aria-disabled={uploadDisabled}
            onClick={() =>
              !isUploading && !uploadDisabled && inputRef.current?.click()
            }
            onDragOver={(e) => {
              e.preventDefault();
              if (!uploadDisabled) setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={onDrop}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              padding: "40px 24px",
              borderRadius: "12px",
              cursor: uploadDisabled
                ? "not-allowed"
                : isUploading
                  ? "default"
                  : "pointer",
              opacity: uploadDisabled ? 0.5 : 1,
              ...(isAccentDropzone
                ? { backgroundImage: accentDropzoneBorder }
                : {
                    border: `1.5px dashed ${
                      isDragOver
                        ? theme.palette.primary.main
                        : theme.palette.divider
                    }`,
                  }),
              backgroundColor: isDragOver
                ? alpha(theme.palette.primary.main, 0.06)
                : "transparent",
              transition: "border-color .15s, background-color .15s",
            }}
          >
            {isUploading ? (
              <>
                <CircularProgress size={28} />
                <Typography
                  sx={{ fontSize: "13px", color: theme.palette.text.primary }}
                >
                  {t("uploading")}
                </Typography>
              </>
            ) : (
              <>
                <Upload size={26} color={theme.palette.primary.main} />
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 700,
                    color: isAccentDropzone
                      ? theme.palette.text.primary
                      : theme.palette.primary.main,
                  }}
                >
                  {t("dropzone-title")}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "13px",
                    color: theme.palette.text.primary,
                    textAlign: "center",
                  }}
                >
                  {uploadDisabled
                    ? t("upload-not-allowed")
                    : t("dropzone-text")}
                </Typography>
              </>
            )}
            <input
              ref={inputRef}
              type="file"
              accept={accept}
              hidden
              disabled={uploadDisabled}
              onChange={onPickFile}
            />
          </Box>
        )}

        {discards && discardsVariant === "light" && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              padding: "16px",
              borderRadius: "4px",
              backgroundColor: theme.palette.badge.warning.background,
              border: `1px solid ${theme.palette.badge.warning.border}`,
            }}
          >
            <CircleAlert
              size={20}
              color={theme.palette.badge.warning.border}
              style={{ flexShrink: 0 }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                flex: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 700,
                  color: BANNER_TEXT_COLOR,
                }}
              >
                {t("discards-title")}
              </Typography>
              <Typography sx={{ fontSize: "14px", color: BANNER_TEXT_COLOR }}>
                {discards.discarded != null && discards.total != null ? (
                  <>
                    <Box component="strong" sx={{ fontWeight: 700 }}>
                      {discards.discarded}/{discards.total}
                    </Box>{" "}
                    {t("discards-text-count")}
                  </>
                ) : (
                  t("discards-text")
                )}
              </Typography>
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<Download size={16} />}
                  onClick={handleDownloadDiscards}
                  sx={{
                    height: "40px",
                    padding: "0 16px",
                    fontSize: "0.875rem",
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    textTransform: "none",
                    borderRadius: "8px",
                    border: `2px solid ${BANNER_TEXT_COLOR}`,
                    color: BANNER_TEXT_COLOR,
                    "&:hover": {
                      border: `2px solid ${BANNER_TEXT_COLOR}`,
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  {t("discards-download-short")}
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {discards && discardsVariant !== "light" && (
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              padding: "16px",
              borderRadius: "12px",
              backgroundColor: alpha(theme.palette.warning.main, 0.12),
              border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
            }}
          >
            <TriangleAlert
              size={18}
              color={theme.palette.warning.main}
              style={{ flexShrink: 0, marginTop: 2 }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                flex: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                }}
              >
                {t("discards-title")}
              </Typography>
              <Typography
                sx={{ fontSize: "12px", color: theme.palette.text.primary }}
              >
                {t("discards-text")}
              </Typography>
              <Box>
                <ButtonSecondary
                  size="md"
                  startIcon={<Download size={16} />}
                  onClick={handleDownloadDiscards}
                >
                  {t("discards-download")}
                </ButtonSecondary>
              </Box>
            </Box>
          </Box>
        )}

        {downloadActions && downloadActions.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
            }}
          >
            {downloadActions.map((action, index) => {
              const ActionButton =
                action.variant === "primary" ? ButtonPrimary : ButtonSecondary;
              const isActionDownloading = downloadingActions.includes(index);
              return (
                <ActionButton
                  key={action.label}
                  size="md"
                  endIcon={
                    isActionDownloading ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      (action.icon ?? <Download size={16} />)
                    )
                  }
                  onClick={() => handleDownloadAction(action, index)}
                  disabled={isActionDownloading}
                  sxButton={{ flex: 1 }}
                >
                  {action.label}
                </ActionButton>
              );
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UploadModalCst;
